import React, { useState } from 'react';

const MMSE_ITEMS = [
  { domain: "Orientation (Time)", max: 5, question: "What is the year? Season? Date? Day of the week? Month?" },
  { domain: "Orientation (Place)", max: 5, question: "Where are we now: State? County? Town/city? Hospital? Floor?" },
  { domain: "Registration", max: 3, question: "Name 3 unrelated objects. Patient repeats all three. Score 1 per correct. Number of trials: ___" },
  { domain: "Attention & Calc.", max: 5, question: "Count backward from 100 by sevens. (93, 86, 79, 72, 65…) Stop after 5. Alternative: Spell WORLD backwards (D-L-R-O-W)." },
  { domain: "Recall", max: 3, question: "Earlier I told you the names of three things. Can you tell me what those were?" },
  { domain: "Naming", max: 2, question: "Show the patient two simple objects (watch + pencil) and ask them to name them." },
  { domain: "Repetition", max: 1, question: "Repeat the phrase: 'No ifs, ands, or buts.'" },
  { domain: "3-Stage Command", max: 3, question: "Take the paper in your right hand, fold it in half, and put it on the floor." },
  { domain: "Reading", max: 1, question: "Please read this and do what it says. (Written instruction is 'Close your eyes.')" },
  { domain: "Writing", max: 1, question: "Make up and write a sentence about anything. (Must contain a noun and a verb.)" },
  { domain: "Visuospatial / Copy", max: 1, question: "Please copy this picture. (Two overlapping pentagons. All 10 angles present and two must intersect.)" },
];

const MMSEScorer = ({ value = {}, onChange }) => {
  const [open, setOpen] = useState(false);

  const total = MMSE_ITEMS.reduce((sum, item) => sum + (value[item.domain] ?? 0), 0);
  const maxTotal = 30;
  const scoredCount = MMSE_ITEMS.filter(i => value[i.domain] !== null && value[i.domain] !== undefined).length;

  const getSeverity = (score) => {
    if (score >= 24) return { label: 'Normal cognition', color: '#059669', bg: '#ecfdf5', border: '#a7f3d0' };
    if (score >= 19) return { label: 'Mild impairment', color: '#d97706', bg: '#fffbeb', border: '#fcd34d' };
    if (score >= 10) return { label: 'Moderate impairment', color: '#ea580c', bg: '#fff7ed', border: '#fdba74' };
    return { label: 'Severe impairment', color: '#dc2626', bg: '#fef2f2', border: '#fca5a5' };
  };

  const severity = getSeverity(total);

  const setScore = (domain, score) => {
    onChange?.({ ...value, [domain]: score });
  };

  return (
    <div style={{ marginBottom: '8px' }}>
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} style={{
          width: '100%', padding: '15px', borderRadius: '14px',
          border: '1px solid #e2e8f0', backgroundColor: 'transparent', color: '#3730a3',
          fontSize: '0.95rem', fontWeight: '700', cursor: 'pointer',
          display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', transition: 'all 0.2s'
        }}>
          + MMSE (Cognitive Assessment)
          {scoredCount > 0 && (
            <span style={{ fontSize: '11px', padding: '2px 10px', borderRadius: '20px', background: '#3730a3', color: '#fff', fontWeight: 800 }}>
              {scoredCount}/11 scored
            </span>
          )}
          {scoredCount > 0 && (
            <span style={{ fontSize: '11px', padding: '2px 10px', borderRadius: '20px', background: severity.color, color: '#fff', fontWeight: 800 }}>
              {total}/30 — {severity.label}
            </span>
          )}
        </button>
      ) : (
        <div style={{ padding: '20px' }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
            <span style={{ fontWeight: '800', fontSize: '1rem', color: '#3730a3' }}>🧠 MMSE — Mini Mental State Examination</span>
            <button type="button" onClick={() => setOpen(false)} style={{
              background: '#e0e7ff', border: 'none', borderRadius: '50%', width: '32px', height: '32px',
              cursor: 'pointer', fontWeight: '700', color: '#4338ca', fontSize: '1.1rem'
            }}>×</button>
          </div>

          {/* Score summary bar */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '16px',
            padding: '14px 18px', borderRadius: '12px',
            background: severity.bg, border: `1px solid ${severity.border}`,
            marginBottom: '16px'
          }}>
            <div style={{ textAlign: 'center', minWidth: '64px' }}>
              <div style={{ fontSize: '2.2rem', fontWeight: '800', color: severity.color, lineHeight: 1 }}>{total}</div>
              <div style={{ fontSize: '11px', color: severity.color, fontWeight: 600 }}>/ {maxTotal}</div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ height: '8px', background: '#e5e7eb', borderRadius: '99px', marginBottom: '8px', overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: '99px',
                  width: `${(total / maxTotal) * 100}%`,
                  background: severity.color, transition: 'width 0.3s ease'
                }} />
              </div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: severity.color }}>{severity.label}</div>
              <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                Normal ≥24 · Mild 19–23 · Moderate 10–18 · Severe &lt;10
              </div>
            </div>
          </div>

          {/* Table */}
          <div style={{ overflowX: 'auto', borderRadius: '12px' }}>
          <div style={{ border: '0.5px solid #c7d2fe', background: '#fff', minWidth: '420px' }}>
            <div style={{
              display: 'grid', gridTemplateColumns: '180px 1fr 100px',
              background: '#f8fafc', borderBottom: '0.5px solid #e2e8f0',
              padding: '8px 14px'
            }}>
              {['Domain', 'Question / Instruction', 'Score'].map(h => (
                <div key={h} style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textAlign: h === 'Score' ? 'center' : 'left' }}>{h}</div>
              ))}
            </div>

            {MMSE_ITEMS.map((item, idx) => {
              const score = value[item.domain] ?? null;
              const filled = score !== null;
              return (
                <div key={item.domain} style={{
                  display: 'grid', gridTemplateColumns: '180px 1fr 100px',
                  alignItems: 'start', padding: '10px 14px',
                  borderBottom: idx < MMSE_ITEMS.length - 1 ? '0.5px solid #f1f5f9' : 'none',
                  background: filled ? '#fafbff' : '#fff', transition: 'background 0.15s'
                }}>
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>{item.domain}</div>
                    <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>Max: {item.max}</div>
                  </div>
                  <div style={{ fontSize: '12px', color: '#475569', lineHeight: 1.5, paddingRight: '12px' }}>
                    {item.question}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'center' }}>
                    {Array.from({ length: item.max + 1 }, (_, i) => i).map(n => {
                      const isSelected = score === n;
                      const btnColor = n === 0 ? '#dc2626' : n === item.max ? '#059669' : '#d97706';
                      return (
                        <button key={n} type="button"
                          onClick={() => setScore(item.domain, isSelected ? null : n)}
                          style={{
                            width: '28px', height: '28px', borderRadius: '8px',
                            border: `1.5px solid ${isSelected ? btnColor : '#e2e8f0'}`,
                            background: isSelected ? btnColor : 'transparent',
                            color: isSelected ? '#fff' : '#64748b',
                            fontSize: '12px', fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s'
                          }}
                        >{n}</button>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            <div style={{
              display: 'grid', gridTemplateColumns: '180px 1fr 100px',
              padding: '10px 14px', background: '#f1f5f9',
              borderTop: '1.5px solid #e2e8f0', alignItems: 'center'
            }}>
              <div style={{ fontSize: '13px', fontWeight: 800, color: '#0f172a' }}>TOTAL</div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>
                {scoredCount} / {MMSE_ITEMS.length} domains scored
              </div>
              <div style={{ textAlign: 'center', fontSize: '18px', fontWeight: 800, color: severity.color }}>
                {total} / {maxTotal}
              </div>
            </div>
          </div>

          </div>

          {scoredCount > 0 && (
            <button type="button" onClick={() => onChange({})} style={{
              marginTop: '10px', fontSize: '12px', background: 'none', border: 'none',
              color: '#94a3b8', cursor: 'pointer', textDecoration: 'underline'
            }}>
              Clear all MMSE data
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default MMSEScorer;
